import { useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';
import TableManager from '../TableManager/TableManager';
import { COURSE_ASSIGNMENT_TEAMS_HEADINGS } from '../../../../lib/constants/courses';
import { teamsAdapter } from './CourseTeams.utils';
import { ASSIGN_COURSE_LABELS, REPORT_DIALOG_LABELS, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import { CourseTeamsDialogType, CourseTeamsProps } from './CourseTeams.types';
import styles from './CourseTeams.module.scss';
import { adaptArrayToTableObject, baseSearchFn } from '../TableManager/TableManager.utils';
import { useDeleteTeamFromTraining } from '../../../../lib/api/postApi';
import { useSnackbarContext } from '../../../../lib/context/SnackbarContext';
import { NotificationType } from '../Notification/Notification';
import { ConfirmDialog } from '../../../common/ConfirmDialog/ConfirmDialog';
import { useTrainingTeamsSummary } from '../../../../lib/api/swrApi';
import { isNil } from '../../../../lib/utils/isNil';
import { PureTableActionBaseHandlerPayload } from '../../../common/PureTable/PureTable.types';
import { useTrainingTeamMembersReport } from '../../../../lib/api/reportsApi';
import useDownloadReport from '../../../../lib/hooks/useDownloadReport';
import TrainingTeamMemberDialog from './TrainingTeamMemberDialog';
import useFeature from '../../../../lib/hooks/useFeature';
import { replaceObjectPlaceholders } from '../../../../lib/utils/placeholders/replacePlaceholder';
import { FeatureToggleCodename } from '../../../../lib/types/enums';
import { Api } from '../../../../lib/types/apiSpec';
import ErrorBoundary from '../../../common/ErrorBoundary/ErrorBoundary';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

const CourseTeams = ({
  courseDetails,
  type,
  pageSize,
  showPageSizeSelector,
  pageSizeOptions,
  range,
  removeTeamSuccessMessage,
  itemsDeleteState,
  itemsDeleteStateDispatch,
  teamReportConfig,
  teamReportDialogTitle,
}: CourseTeamsProps) => {
  const { id } = courseDetails;
  const { data, mutate: refreshTeams, isLoading, isValidating, error } = useTrainingTeamsSummary(id, type, range);
  const fetchDeleteTeam = useDeleteTeamFromTraining(id, type);
  const { mutate } = useSWRConfig();
  const { triggerSnackbar } = useSnackbarContext();
  const [teamDialog, setTeamDialog] = useState<CourseTeamsDialogType>(null);
  const downloadReport = useTrainingTeamMembersReport({ type, id });
  const { wrappedDownloadReport } = useDownloadReport({ reportConfig: teamReportConfig, action: downloadReport });
  const { triggerSnackbarError } = useErrorMapping();
  const membersPreviewEnabled = useFeature(FeatureToggleCodename.trainingTeamMembersDetails);
  const teamDialogData =
    teamDialog?.type === 'preview' ? data?.find(({ id: teamId }) => teamId === teamDialog.id) : null;
  const hideOverdue = useFeature(FeatureToggleCodename.hideOverdueDates);
  const tableHeadings = COURSE_ASSIGNMENT_TEAMS_HEADINGS.filter(heading => !hideOverdue || heading !== 'totalOverdue');

  const rowActionHandler = async ({ type: actionType, id: teamId }: PureTableActionBaseHandlerPayload) => {
    switch (actionType) {
      case 'delete.team':
        setTeamDialog({ id: teamId, type: 'delete' });
        break;
      case 'download.teamTrainingMembers':
        itemsDeleteStateDispatch({ type: 'deleteStateAdd', payload: teamId });
        await wrappedDownloadReport(teamId).finally(() =>
          itemsDeleteStateDispatch({ type: 'deleteStateRemove', payload: teamId }),
        );
        break;
      case 'openDetails.teamTrainingMembers':
        setTeamDialog({ id: teamId, type: 'preview' });
        break;
      default:
        break;
    }
  };

  const deleteTeam = () => {
    if (!teamDialog) {
      return;
    }
    itemsDeleteStateDispatch({ type: 'deleteStateAdd', payload: teamDialog.id });
    fetchDeleteTeam(teamDialog.id)
      .then(() => {
        triggerSnackbar(removeTeamSuccessMessage, NotificationType.Success);
        refreshTeams();
        mutate(([key]: string[]) =>
          key.endsWith(`${type === Api.LearningType.Course ? 'courses' : 'learningpaths'}/${id}/teams`),
        );
      })
      .catch(err => {
        triggerSnackbarError(err);
      })
      .finally(() => itemsDeleteStateDispatch({ type: 'deleteStateRemove', payload: teamDialog.id }));
    setTeamDialog(null);
  };

  const tableData = useMemo(
    () =>
      data
        ?.map(
          teamsAdapter(itemsDeleteState)({
            reportEnabled: !!teamReportConfig,
            hideOverdue: !!hideOverdue,
            previewEnabled: !!membersPreviewEnabled,
            actionHandler: rowActionHandler,
          }),
        )
        .reduce(adaptArrayToTableObject(tableHeadings), {}),
    [type, data, itemsDeleteState, teamReportConfig],
  );

  const selected = hideOverdue
    ? []
    : data?.reduce<string[]>(
        (acc, { id: selectedId = '', totalOverdue }) => [...acc, ...(totalOverdue ? [selectedId] : [])],
        [],
      );

  const commonTableProps = {
    pageSize,
    showPageSizeSelector,
    pageSizeOptions,
  };

  return (
    <ErrorBoundary error={error} internal>
      <TableManager
        className={styles.courseTeamsTable}
        headings={tableHeadings.slice(0, -1)}
        data={tableData ?? {}}
        selected={selected}
        searchBy={baseSearchFn(0)}
        searchPlaceholderKey={COMMON_LABELS.SEARCH.TEAM}
        isInitialLoad={isLoading}
        isLoading={isValidating && !isNil(data)}
        {...commonTableProps}
      />
      <ConfirmDialog
        onCancel={() => setTeamDialog(null)}
        onConfirm={deleteTeam}
        labelKeys={{
          dialogTitle: ASSIGN_COURSE_LABELS.CONFIRM_TEAM_DIALOG_TITLE,
          content: ASSIGN_COURSE_LABELS.CONFIRM_TEAM_DIALOG_CONTENT,
          confirmButton: ASSIGN_COURSE_LABELS.CONFIRM_DIALOG_CONFIRM_BUTTON,
        }}
        isOpen={teamDialog?.type === 'delete'}
      />
      {teamDialog?.type === 'preview' && (
        <TrainingTeamMemberDialog
          {...commonTableProps}
          type={type}
          id={id}
          teamid={teamDialog.id}
          onClose={() => setTeamDialog(null)}
          dialogTitle={replaceObjectPlaceholders(teamDialogData ?? {}, teamReportDialogTitle ?? '')}
          {...(teamReportConfig && {
            action: () => rowActionHandler({ type: 'download.teamTrainingMembers', id: teamDialog.id }),
          })}
          searchPlaceholderKey={COMMON_LABELS.SEARCH.TEAM_MEMBER}
          actionLabelKey={REPORT_DIALOG_LABELS.ACTION_BUTTON('training-teammembers')}
        />
      )}
    </ErrorBoundary>
  );
};

export default CourseTeams;
